import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { AuthService } from "@/services/authService";
import { currentUser } from "@/redux/reducers/AuthReducer"; // ✅ import the thunk
import { resolveLandingPath } from "@/routes/landing";

export const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
    <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      fill="#34A853"
    />
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

const GoogleCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    const storedState = localStorage.getItem("oauth_state");
    localStorage.removeItem("oauth_state");

    if (!code || !state) {
      toast.error("Google sign in was cancelled or failed.");
      navigate("/login?error=oauth_failed");
      return;
    }

    if (!storedState) {
      toast.error("Session expired. Please try signing in again.");
      navigate("/login?error=oauth_failed");
      return;
    }

    AuthService.handleGoogleCallback(code, state, storedState)
      .then(async (data) => {
        // ✅ accessToken is already saved to sessionStorage inside handleGoogleCallback
        // Now fetch current user to populate Redux state
        const result = await dispatch(currentUser());

        if (currentUser.fulfilled.match(result)) {
          toast.success(`Welcome, ${data.user?.name}!`);
          // Resolve the destination from the account that just signed in.
          // /admin/dashboard is wrong for a SuperAdmin (RouteGuard bounces them
          // to /super-admin/*, showing the tenant shell on the way) and wrong
          // for anyone whose role lacks dashboard.view — they would land on
          // "Unauthorized" straight after a successful sign-in.
          navigate(resolveLandingPath(result.payload?.user), { replace: true });
        } else {
          toast.error("Failed to load user profile. Please try again.");
          navigate("/login?error=oauth_failed");
        }
      })
      .catch((err) => {
        console.error("Google callback error:", err?.response?.data);
        const msg = err?.response?.data?.message || "Google sign in failed. Please try again.";
        toast.error(msg);
        navigate("/login?error=oauth_failed");
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
        <p className="text-sm text-white/60">Completing Google sign in...</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
